import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { routesAPI } from '../services/api';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Routes = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editRoute, setEditRoute] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [error, setError] = useState('');

  const { data: routesData, isLoading } = useQuery('routes', routesAPI.getAll);

  const createMutation = useMutation(routesAPI.create, {
    onSuccess: () => { setShowModal(false); setForm({ name: '' }); queryClient.invalidateQueries('routes'); },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create route'),
  });
  const updateMutation = useMutation(({ id, ...data }) => routesAPI.update(id, data), {
    onSuccess: () => { setShowModal(false); setEditRoute(null); setForm({ name: '' }); queryClient.invalidateQueries('routes'); },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update route'),
  });
  const deleteMutation = useMutation(routesAPI.delete, {
    onSuccess: () => queryClient.invalidateQueries('routes'),
    onError: (err) => setError(err.response?.data?.error || 'Failed to delete route'),
  });

  const openAdd = () => { setEditRoute(null); setForm({ name: '' }); setShowModal(true); setError(''); };
  const openEdit = (route) => { setEditRoute(route); setForm({ name: route.name }); setShowModal(true); setError(''); };
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name) { setError('Name is required'); return; }
    if (editRoute) updateMutation.mutate({ id: editRoute.id, ...form });
    else createMutation.mutate(form);
  };
  const handleDelete = (id) => { if (window.confirm('Delete this route?')) deleteMutation.mutate(id); };

  if (isLoading) return <LoadingSpinner />;
  const routes = routesData?.routes || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Routes</h1>
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-2" /> Add Route
        </button>
      </div>
      <table className="min-w-full bg-white border rounded-lg">
        <thead>
          <tr>
            <th className="px-4 py-2 border-b">Name</th>
            <th className="px-4 py-2 border-b">Actions</th>
          </tr>
        </thead>
        <tbody>
          {routes.map(route => (
            <tr key={route.id}>
              <td className="px-4 py-2 border-b">{route.name}</td>
              <td className="px-4 py-2 border-b space-x-2">
                <button className="text-blue-600" onClick={() => openEdit(route)}><Edit className="inline h-4 w-4" /></button>
                <button className="text-red-600" onClick={() => handleDelete(route.id)}><Trash2 className="inline h-4 w-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">{editRoute ? 'Edit Route' : 'Add Route'}</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium">Name</label>
                <input type="text" className="w-full border rounded px-3 py-2" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              {error && <div className="text-red-600 text-sm">{error}</div>}
              <div className="flex justify-end space-x-2">
                <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded flex items-center"><Save className="h-4 w-4 mr-1" />Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Routes; 